/**
 * Plays a confetti celebration when the cart crosses the free shipping threshold.
 *
 * The bar is server-rendered, so the unlocked state arrives through cart section
 * re-renders: either as an attribute change on a morphed element, or as a newly
 * connected element (e.g. the first item added to an empty cart). Elements present
 * when the module first runs only record the baseline, so loading a page with a
 * qualifying cart does not celebrate.
 */

const COLORS = ['#f2a6d0', '#e83e8c', '#ffd166', '#06d6a0', '#118ab2', '#ffffff', '#000000'];
const DURATION = 2800;
const PIECES = 160;

let initialized = false;
let lastUnlocked = false;

class FreeShippingBar extends HTMLElement {
  static get observedAttributes() {
    return ['data-unlocked'];
  }

  connectedCallback() {
    this.#sync();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#sync();
  }

  #sync() {
    const unlocked = this.dataset.unlocked === 'true';
    if (initialized && unlocked && !lastUnlocked) {
      celebrate(this.dataset.celebration);
    }
    lastUnlocked = unlocked;
  }
}

/**
 * @param {string | undefined} style - 'burst' or 'rain'
 */
function celebrate(style) {
  if (style !== 'burst' && style !== 'rain') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100vw',
    height: '100vh',
    maxWidth: 'none',
    maxHeight: 'none',
    margin: '0',
    padding: '0',
    border: '0',
    background: 'transparent',
    overflow: 'visible',
    pointerEvents: 'none',
    zIndex: '2147483647',
  });

  // An open modal dialog (the cart drawer) sits in the browser's top layer, above any z-index.
  // Showing the canvas as a popover puts it in the top layer too, stacked above the dialog.
  // It is appended inside the modal so it is not part of the inert page behind it.
  const host = document.querySelector('dialog:modal') ?? document.body;
  host.append(canvas);
  if ('showPopover' in canvas) {
    canvas.popover = 'manual';
    canvas.showPopover();
  }

  const context = canvas.getContext('2d');
  if (!context) {
    canvas.remove();
    return;
  }

  const ratio = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * ratio;
  canvas.height = height * ratio;
  context.scale(ratio, ratio);

  const pieces = Array.from({ length: PIECES }, () => createPiece(style, width, height));
  const start = performance.now();
  let previous = start;

  /** @param {number} now */
  const frame = (now) => {
    const elapsed = now - start;
    const delta = Math.min((now - previous) / 16.67, 3);
    previous = now;

    context.clearRect(0, 0, width, height);
    // Fade out over the final 600ms
    context.globalAlpha = Math.max(0, Math.min(1, (DURATION - elapsed) / 600));

    for (const piece of pieces) {
      piece.vx *= 0.99;
      piece.vy = piece.vy * 0.99 + 0.25 * delta;
      piece.x += (piece.vx + Math.sin(piece.wobble) * 0.6) * delta;
      piece.y += piece.vy * delta;
      piece.wobble += 0.1 * delta;
      piece.rotation += piece.spin * delta;

      context.save();
      context.translate(piece.x, piece.y);
      context.rotate(piece.rotation);
      // Scaling Y by cos(wobble) fakes the paper flipping as it falls
      context.scale(1, Math.cos(piece.wobble));
      context.fillStyle = piece.color;
      context.fillRect(-piece.width / 2, -piece.height / 2, piece.width, piece.height);
      context.restore();
    }

    if (elapsed < DURATION) {
      requestAnimationFrame(frame);
    } else {
      canvas.remove();
    }
  };

  requestAnimationFrame(frame);
}

/**
 * @param {string} style
 * @param {number} width
 * @param {number} height
 */
function createPiece(style, width, height) {
  const color = COLORS[Math.floor(Math.random() * COLORS.length)] ?? '#e83e8c';
  const isStripe = Math.random() < 0.5;
  const base = {
    color,
    width: isStripe ? 4 + Math.random() * 3 : 7 + Math.random() * 5,
    height: isStripe ? 12 + Math.random() * 8 : 7 + Math.random() * 5,
    rotation: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.3,
    wobble: Math.random() * Math.PI * 2,
  };

  if (style === 'rain') {
    return {
      ...base,
      x: Math.random() * width,
      y: -20 - Math.random() * height * 0.5,
      vx: (Math.random() - 0.5) * 2,
      vy: 2 + Math.random() * 3,
    };
  }

  // Burst: fire outward and upward from the center of the viewport
  const angle = Math.random() * Math.PI * 2;
  const speed = 6 + Math.random() * 10;
  return {
    ...base,
    x: width / 2,
    y: height / 2,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed - 6,
  };
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

// Elements upgraded during `define` above are the initial page state; only later changes celebrate.
initialized = true;
